/**
 * AdminDashboard Component — Layout Shell (Refreshed)
 * Sidebar + content area. Routes: Dashboard, Studies, Reports Hub, Leadership, Settings
 */

import React, { useState, useCallback, useEffect } from 'react';
import { AlertTriangle, CheckCircle2, Lock, Loader2, RefreshCw, Unlock } from 'lucide-react';
import Sidebar from './Sidebar';
import DashboardOverview from './DashboardOverview';
import SurveysList from './SurveysList';
import ReportsHub from './ReportsHub';
import LeadershipReportsPage from './LeadershipReportsPage';
import UserManagement from './UserManagement';
import { getReportGenerationLock, lockReportGeneration, unlockReportGeneration } from '../services/api';
import '../styles/design-system.css';

function ReportGenerationLockControl({ status, loading, busy, error, onToggle, onRefresh }) {
    const locked = status?.locked;
    const message = status?.message || 'Report generation status is unavailable.';

    return (
        <div className="ds-card" style={{ margin: '16px 24px 0', borderColor: locked ? 'var(--color-warning)' : 'var(--color-border)' }}>
            <div className="ds-card-body" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '12px 16px' }}>
                <span className={`ds-card-icon ${locked ? 'warning' : 'success'}`}>
                    {locked ? <Lock size={16} /> : <CheckCircle2 size={16} />}
                </span>
                <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: 'var(--color-text-primary)', fontSize: 'var(--font-size-sm)' }}>
                        Report Generation {locked ? 'Locked' : 'Available'}
                    </div>
                    <div style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--font-size-xs)', lineHeight: 1.4 }}>
                        {message}
                        {locked && status?.locked_by && ` Locked by ${status.locked_by}${status.locked_at ? ` on ${new Date(status.locked_at).toLocaleString()}` : ''}.`}
                    </div>
                    {error && (
                        <div className="ds-badge ds-badge-error" style={{ display: 'inline-flex', marginTop: 6 }}>
                            <AlertTriangle size={12} /> {error}
                        </div>
                    )}
                </div>
                <button className="ds-btn ds-btn-ghost" onClick={onRefresh} disabled={loading || busy} title="Refresh lock status">
                    {loading ? <Loader2 size={14} className="ds-spinner" /> : <RefreshCw size={14} />}
                    Refresh
                </button>
                <button className={`ds-btn ${locked ? 'ds-btn-primary' : 'ds-btn-secondary'}`} onClick={onToggle} disabled={loading || busy}>
                    {busy ? <Loader2 size={14} className="ds-spinner" /> : locked ? <Unlock size={14} /> : <Lock size={14} />}
                    {locked ? 'Unlock Reports' : 'Lock Reports'}
                </button>
            </div>
        </div>
    );
}

function AdminDashboard({ adminUser, onLogout }) {
    const [activeView, setActiveView] = useState('overview');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebar-collapsed') === 'true');
    const [reportGenerationLock, setReportGenerationLock] = useState(null);
    const [lockLoading, setLockLoading] = useState(true);
    const [lockBusy, setLockBusy] = useState(false);
    const [lockError, setLockError] = useState('');

    const handleToggleSidebar = useCallback(() => {
        setSidebarCollapsed(prev => {
            const next = !prev;
            localStorage.setItem('sidebar-collapsed', String(next));
            return next;
        });
    }, []);

    const refreshReportGenerationLock = useCallback(async () => {
        setLockLoading(true);
        setLockError('');
        try {
            const status = await getReportGenerationLock();
            setReportGenerationLock(status);
        } catch (err) {
            setLockError(err.message || 'Unable to load lock status');
        } finally {
            setLockLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshReportGenerationLock();
    }, [refreshReportGenerationLock]);

    const handleToggleReportGenerationLock = useCallback(async () => {
        setLockBusy(true);
        setLockError('');
        try {
            const nextStatus = reportGenerationLock?.locked
                ? await unlockReportGeneration()
                : await lockReportGeneration();
            setReportGenerationLock(nextStatus);
        } catch (err) {
            setLockError(err.message || 'Unable to update lock status');
        } finally {
            setLockBusy(false);
        }
    }, [reportGenerationLock]);

    // Sync activeView with current URL on mount
    useEffect(() => {
        const path = window.location.pathname;
        if (path.startsWith('/admin/surveys')) setActiveView('surveys');
        else if (path.startsWith('/admin/reports') || path.startsWith('/admin/report-management')) setActiveView('reports');
        else if (path.startsWith('/admin/leadership')) setActiveView('leadership');
        else if (path.startsWith('/admin/settings')) setActiveView('settings');
        else setActiveView('overview');
    }, []);

    const handleNavigate = useCallback((viewId) => {
        setActiveView(viewId);
        const paths = {
            overview: '/admin',
            surveys: '/admin/surveys',
            reports: '/admin/reports',
            leadership: '/admin/leadership',
            settings: '/admin/settings',
        };
        window.history.pushState({}, '', paths[viewId] || '/admin');
    }, []);

    const renderContent = () => {
        switch (activeView) {
            case 'overview':
                return <DashboardOverview adminUser={adminUser} onNavigate={handleNavigate} />;
            case 'surveys':
                return <SurveysList adminUser={adminUser} onLogout={onLogout} />;
            case 'reports':
                return <ReportsHub reportGenerationLock={reportGenerationLock} />;
            case 'leadership':
                return <LeadershipReportsPage reportGenerationLock={reportGenerationLock} />;
            case 'settings':
                return <UserManagement />;
            default:
                return <DashboardOverview adminUser={adminUser} onNavigate={handleNavigate} />;
        }
    };

    return (
        <div className="ds-layout">
            <Sidebar
                activeView={activeView}
                onNavigate={handleNavigate}
                adminUser={adminUser}
                onLogout={onLogout}
                collapsed={sidebarCollapsed}
                onToggleCollapse={handleToggleSidebar}
            />
            <main className={`ds-main ${sidebarCollapsed ? 'collapsed' : ''}`}>
                <ReportGenerationLockControl
                    status={reportGenerationLock}
                    loading={lockLoading}
                    busy={lockBusy}
                    error={lockError}
                    onToggle={handleToggleReportGenerationLock}
                    onRefresh={refreshReportGenerationLock}
                />
                {renderContent()}
            </main>
        </div>
    );
}

export default AdminDashboard;
